"use client";

import React from "react";
import { Clock, ArrowRight, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const COURSES_DATA = [{
  id: "electricista",
  title: "Técnico Electricista",
  description: "Formación integral en instalaciones eléctricas, normativas vigentes y mantenimiento industrial con enfoque práctico.",
  duration: "6 Meses",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1583410/img/curso_electricista_tablero.png",
  accentClass: "bg-secondary text-secondary-foreground hover:bg-secondary/90",
  link: "https://ecap.edu.uy/tecnico-electricista/"
}, {
  id: "barberia",
  title: "Barbería Profesional",
  description: "Domina el arte del corte masculino, perfilado de barba y gestión de salón con las últimas tendencias mundiales.",
  duration: "4 Meses",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1583410/img/curso_barberia_practica.png",
  accentClass: "bg-secondary text-secondary-foreground hover:bg-secondary/90",
  link: "https://ecap.edu.uy/barberia/"
}, {
  id: "kinesiologia",
  title: "Kinesiología Deportiva",
  description: "Especialízate en la prevención y tratamiento de lesiones en el ámbito deportivo con técnicas de vanguardia.",
  duration: "5 Meses",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1583410/img/curso_kinesiologia_terapia.png",
  accentClass: "bg-secondary text-secondary-foreground hover:bg-secondary/90",
  link: "https://ecap.edu.uy/kinesiologia-deportiva/"
}, {
  id: "masoterapia",
  title: "Masoterapia",
  description: "Aprende técnicas de masaje terapéutico, relajante y descontracturante para una salida laboral inmediata.",
  duration: "4 Meses",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1583410/img/curso_masoterapia_sesion.png",
  accentClass: "bg-secondary text-secondary-foreground hover:bg-secondary/90",
  link: "https://ecap.edu.uy/masoterapia/"
}];
export default function FeaturedCourses() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="105060" id="cursos-destacados" className="py-24 bg-background text-foreground relative overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none opacity-[0.02] bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1583410/img/grid_pattern_industrial.png)] bg-repeat" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-semibold mb-6 border border-primary/20 backdrop-blur-sm">
            <Award className="w-4 h-4" />
            <span className="uppercase tracking-wider">Más de 10 años de trayectoria</span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-foreground tracking-tighter mb-6 uppercase">
            Cursos Destacados
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-medium leading-relaxed">
            Fórmate con los mejores profesionales en Uruguay. Nuestra academia ECAP te ofrece capacitación técnica de alto nivel orientada a la rápida inserción laboral en todo el país.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {COURSES_DATA.map((course, index) => <CourseCard key={course.id} course={course} index={index} />)}
        </div>

        <div className="mt-16 text-center">
          <Button asChild variant="outline" size="lg" className="border-primary/30 text-foreground hover:bg-primary/5 rounded-full px-8 py-6 text-lg font-bold transition-all duration-300">
            <Link to="https://ecap.edu.uy/oferta-academica">
              Ver toda la oferta académica
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}
function CourseCard({
  course,
  index
}: {
  course: typeof COURSES_DATA[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const delay = index * 150;
  return <Link to={course.link} asChild>
      <div data-index={index} ref={ref} style={{
      transitionDelay: `${delay}ms`
    }} className={`group relative h-[450px] rounded-3xl overflow-hidden bg-card shadow-2xl border border-border/50 transition-all duration-700 ease-out cursor-pointer ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        {}
        <img src={course.image} alt={course.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 ease-in-out" />
        
        {}
        <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-background/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 group-hover:from-background/90 group-hover:via-background/60" />

        {}
        <div className="absolute inset-0 p-8 flex flex-col justify-end">
          <div className="transform transition-transform duration-500 ease-out group-hover:-translate-y-2">
            <h3 className="text-2xl md:text-3xl font-extrabold text-white mb-3 leading-tight tracking-tight uppercase">
              {course.title}
            </h3>
            
            {}
            <div className="grid grid-rows-[0fr] group-hover:grid-rows-[1fr] transition-[grid-template-rows] duration-500 ease-in-out">
              <div className="overflow-hidden">
                <div className="pt-2 pb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-100">
                  <p className="text-gray-300 text-sm md:text-base mb-6 line-clamp-3 font-medium leading-relaxed">
                    {course.description}
                  </p>
                  
                  <div className="flex items-center gap-2 text-primary mb-6 text-sm font-bold font-mono">
                    <Clock className="w-4 h-4" />
                    <span>DURACIÓN: {course.duration}</span>
                  </div>

                  <Button asChild className={`w-full rounded-xl shadow-lg font-bold py-6 transition-all duration-300 ${course.accentClass}`}>
                    <Link to={course.link} className="flex items-center justify-center">
                      Ver Programa
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Link>;
}